import {
  Avatar,
  Button,
  Card,
  Form,
  InlineGrid,
  InlineStack,
  Link,
  Popover,
  Text,
  TextField,
  Toast,
  BlockStack,
  ExceptionList,
} from '@shopify/polaris';
import QuickUploadImage from 'components/oneclick-upload-image';
import { useCallback, useEffect, useState } from 'react';

import helpers from 'helpers';
import dateandtime from 'date-and-time';
import { StatusActiveIcon, AlertCircleIcon, PlusCircleIcon } from '@shopify/polaris-icons';
import CustomerAssign from './customer.assign';
import QuickSearchUser from 'components/quickSearchUser';
import __ from 'languages/index';
import { TypedCustomer } from 'interface/customer.model';
import __helpers from 'helpers';
import { useUpdateCustomer } from 'queries/customer.query';
import Capacity from 'components/Capacity';

export default function CustomerGeneralDetail({ entity }: { entity: TypedCustomer }) {
  const { mutateAsync: updateEntity, isSuccess: updateSuccess, isPending: updating } = useUpdateCustomer();

  const [internalErrorNotice, setInternalErrorNotice] = useState('');
  const [srcImageAvatar, setSrcImageAvatar] = useState('');

  useEffect(() => {
    setSrcImageAvatar(entity?.user_avatar);
  }, [entity]);

  const handQuickUpdateSuccess = useCallback(
    async (res: any) => {
      setSrcImageAvatar(res.media_url);
      try {
        await updateEntity({ user_id: entity?.user_id, user_avatar: res.media_url });
      } catch (e) {}
    },
    [entity]
  );

  const handUploadError = useCallback((err: any) => {
    setInternalErrorNotice(err);
  }, []);

  /**
   * Quick note
   */
  const [showQuickNote, setShowQuickNote] = useState(false);
  const [biotext, setBiotext] = useState('');

  // lấy quyền

  useEffect(() => {
    if (entity) {
      setBiotext(entity.bio || '');
    }
  }, [entity]);

  useEffect(() => {
    if (updateSuccess === true) {
      setShowQuickNote(false);
    }
  }, [updateSuccess]);

  const saveQuickNoteButtonCallback = useCallback(async () => {
    try {
      await updateEntity({ user_id: entity?.user_id, bio: biotext });
    } catch (e) {}
  }, [entity, biotext]);

  const handleQuickNoteChange = useCallback((newValue: string) => setBiotext(newValue), []);

  const [popoverActive1, setPopoverActive1] = useState(false);
  const togglePopoverActive1 = useCallback(() => setPopoverActive1((popoverActive) => !popoverActive), []);

  const [quick_displayname_value, setQuick_displayname_value] = useState('');
  useEffect(() => {
    setQuick_displayname_value(entity?.display_name);
  }, [entity]);

  const savingNewDisplayName = useCallback(async () => {
    //
    togglePopoverActive1();
    try {
      await updateEntity({
        user_id: entity?.user_id,
        display_name: quick_displayname_value,
      });
    } catch (e) {}
  }, [quick_displayname_value]);

  return (
    <>
      {internalErrorNotice && <Toast error content={internalErrorNotice} onDismiss={() => setInternalErrorNotice(null)} />}

      <InlineGrid columns={{ xs: '1', sm: '1', md: '1', lg: ['twoThirds', 'oneThird'] }} gap={'400'}>
        <Card>
          <InlineStack align="start" gap="400" blockAlign="center">
            <div>
              <QuickUploadImage onSuccess={handQuickUpdateSuccess} onError={handUploadError} title="">
                <Avatar name={entity?.display_name} customer source={srcImageAvatar ? __helpers.getMediaLink(srcImageAvatar) : null} size="lg" />
              </QuickUploadImage>
            </div>

            <BlockStack gap={'100'}>
              <Popover
                preferredPosition="below"
                preventCloseOnChildOverlayClick
                preferredAlignment="left"
                autofocusTarget="first-node"
                active={popoverActive1}
                activator={
                  <Text as="h2" variant="headingLg" fontWeight="bold">
                    <Link removeUnderline onClick={togglePopoverActive1}>
                      {entity?.user_title && `(${__(entity?.user_title)})`} {entity?.display_name || entity?.user_login}
                    </Link>
                  </Text>
                }
                onClose={togglePopoverActive1}
                ariaHaspopup={false}
                sectioned
              >
                <div style={{ width: '200px' }}>
                  <Form onSubmit={savingNewDisplayName}>
                    <BlockStack gap="400">
                      <TextField
                        label=""
                        placeholder="Tên khách hàng"
                        value={quick_displayname_value}
                        onChange={(text) => {
                          const formattedText = helpers.capitalizeWords(text);
                          setQuick_displayname_value(formattedText);
                        }}
                        autoComplete="off"
                        clearButton
                        onClearButtonClick={() => setQuick_displayname_value('')}
                      />
                      <Button submit size="slim">
                        Lưu lại
                      </Button>
                    </BlockStack>
                  </Form>
                </div>
              </Popover>
              <InlineStack gap={'200'}>
                <Text as="p" variant="bodySm" tone="subdued">
                  {`Tham gia ` + String(entity ? helpers.subtractDate(entity?.createdAt) : '1') + ' ngày trước'}
                </Text>
              </InlineStack>
            </BlockStack>
          </InlineStack>

          <BlockStack gap={'400'}>
            <div id="admin_customer_gjfhkdu">
              {entity?.user_birthday && (
                <>
                  <Text variant="bodyMd" as="p" fontWeight="bold">
                    Sinh nhật
                  </Text>
                  <Text as="p">
                    {dateandtime.format(new Date(entity?.user_birthday), 'DD-MM-YYYY')} {'  -  '}{' '}
                    {helpers.getAge(new Date(entity?.user_birthday), ' tuổi')}
                  </Text>
                </>
              )}
            </div>

            <div id="admin_customer_bio">
              <Text variant="bodyMd" as="p" fontWeight="bold">
                Bio
              </Text>
              {showQuickNote ? (
                <>
                  <TextField
                    label=""
                    multiline={2}
                    value={biotext}
                    onChange={handleQuickNoteChange}
                    autoComplete="off"
                    maxLength={250}
                    showCharacterCount
                  />
                  <Button variant="plain" onClick={saveQuickNoteButtonCallback} loading={updating}>
                    Save
                  </Button>
                  &nbsp; or &nbsp;
                  <Button
                    variant="plain"
                    onClick={() => {
                      setShowQuickNote(false);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Link removeUnderline monochrome onClick={() => setShowQuickNote(true)}>
                  <Text as="p" tone="subdued">
                    {entity?.bio || 'Khách hàng này chưa có thông tin tự giới thiệu.'}
                  </Text>
                </Link>
              )}
            </div>

            <div id="admin_customer_assign">
              <CustomerAssign customerData={entity} />
            </div>
          </BlockStack>
        </Card>
        <Card>
          <Text as="h3" variant="headingMd">
            Thông tin liên hệ
          </Text>
          <BlockStack gap={'200'}>
            <Text variant="bodyMd" as="p" fontWeight="bold">
              Địa chỉ
            </Text>
            <Text as="p" tone="critical">
              {entity?.user_address ?? 'Chưa có thông tin'}
            </Text>
          </BlockStack>
          <br />
          <BlockStack gap={'200'}>
            <ExceptionList
              items={[
                {
                  title: 'Email',
                  status: 'warning',
                  icon: StatusActiveIcon,
                  description: entity.user_email ? <Link url="#!">{entity.user_email}</Link> : 'Chưa có',
                },

                {
                  title: 'Phone number',
                  status: 'warning',
                  icon: StatusActiveIcon,
                  description: entity.user_phonenumber ? <Link url={`tel:` + entity.user_phonenumber}>{entity.user_phonenumber}</Link> : 'Chưa có',
                },
              ]}
            />
          </BlockStack>
        </Card>
      </InlineGrid>
    </>
  );
}

